"use client"

import { useMemo, useState, useEffect } from 'react'
import { CHART_COLORS_HEX } from '@/lib/utils/chartColors'
import { EmptyState } from '@/components/shared/EmptyState'

interface DevicePieChartProps {
  labels: string[]
  values: number[]
  type: 'OS' | 'Device' | 'Browser'
  onSegmentClick?: (label: string) => void
}

export default function DevicePieChart({ 
  labels = [], 
  values = [], 
  type,
  onSegmentClick 
}: DevicePieChartProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [isDarkMode, setIsDarkMode] = useState(false)

  // Detect dark mode for proper color selection
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'))
    }
    checkDarkMode()
    
    // Watch for theme changes
    const observer = new MutationObserver(checkDarkMode)
    observer.observe(document.documentElement, { 
      attributes: true, 
      attributeFilter: ['class'] 
    })
    
    return () => observer.disconnect()
  }, [])

  // Get colors based on current theme
  const colors = isDarkMode ? CHART_COLORS_HEX.dark : CHART_COLORS_HEX.light
  
  const { chartData, totalVisitors } = useMemo(() => {
    if (!labels.length || !values.length) {
      return { chartData: [], totalVisitors: 0 }
    }

    const total = values.reduce((sum, val) => sum + val, 0)
    
    const data = labels.map((label, index) => ({
      name: label,
      value: values[index] || 0,
      fill: colors[index % colors.length],
      percentage: total > 0 ? ((values[index] / total) * 100) : 0,
    }))

    return { chartData: data, totalVisitors: total }
  }, [labels, values, colors])

  if (!chartData.length) {
    return (
      <div className="h-[200px] flex items-center justify-center">
        <EmptyState
          message="No data to display"
          hint="Please wait for traffic or try a different date range."
          variant="compact"
        />
      </div>
    )
  }

  const handleClick = (name: string) => {
    if (onSegmentClick) {
      onSegmentClick(name)
    }
  }

  // The gauge totals only the segments it charts — one dimension, top entries — so it
  // sits below the period-wide visitor count in the header. Name the coverage instead
  // of leaving two different numbers under the same label.
  const dimensionNoun =
    type === 'OS'
      ? 'operating system'
      : type === 'Device'
        ? 'device type'
        : type === 'Browser'
          ? 'browser'
          : 'category'
  const coverageLabel = `Across ${chartData.length} ${dimensionNoun}${chartData.length === 1 ? '' : 's'}`

  // Calculate arc segments for semi-circle gauge
  const radius = 120
  const strokeWidth = 28
  const centerX = 200
  const centerY = 140
  const startAngle = Math.PI // Start from left (180 degrees)
  const endAngle = 0 // End at right (0 degrees)
  const gapAngle = 0.04 // Small gap between segments

  // Calculate segments
  let currentAngle = startAngle
  const segments = chartData.map((item, index) => {
    const sweepAngle = (item.percentage / 100) * Math.PI - gapAngle
    const segmentStartAngle = currentAngle
    const segmentEndAngle = currentAngle - sweepAngle
    currentAngle = segmentEndAngle - gapAngle

    // Calculate arc path
    const x1 = centerX + radius * Math.cos(segmentStartAngle)
    const y1 = centerY - radius * Math.sin(segmentStartAngle)
    const x2 = centerX + radius * Math.cos(segmentEndAngle)
    const y2 = centerY - radius * Math.sin(segmentEndAngle)
    
    const largeArcFlag = sweepAngle > Math.PI ? 1 : 0
    
    // Calculate label position (middle of arc)
    const labelAngle = (segmentStartAngle + segmentEndAngle) / 2
    const labelRadius = radius + 50
    const labelX = centerX + labelRadius * Math.cos(labelAngle)
    const labelY = centerY - labelRadius * Math.sin(labelAngle)
    
    // Determine text anchor based on position
    const textAnchor = labelX < centerX - 20 ? 'end' : labelX > centerX + 20 ? 'start' : 'middle'

    return {
      ...item,
      index,
      path: `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
      labelX,
      labelY,
      textAnchor,
      midAngle: labelAngle,
    }
  })

  return (
    <div className="w-full flex flex-col items-center py-4 pt-6">
      <svg 
        viewBox="-60 -50 520 270" 
        className="w-full max-w-[500px] h-auto"
        style={{ minHeight: '160px' }}
      >
        {/* Arc segments */}
        {segments.map((segment) => (
          <g key={segment.index}>
            <path
              d={segment.path}
              fill="none"
              stroke={segment.fill}
              strokeWidth={activeIndex === segment.index ? strokeWidth + 6 : strokeWidth}
              strokeLinecap="round"
              className="transition-all duration-200 cursor-pointer"
              style={{
                filter: activeIndex === segment.index ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))' : 'none',
                opacity: activeIndex !== null && activeIndex !== segment.index ? 0.5 : 1,
              }}
              onMouseEnter={() => setActiveIndex(segment.index)}
              onMouseLeave={() => setActiveIndex(null)}
              onClick={() => handleClick(segment.name)}
            />
            
            {/* Labels around the arc */}
            {segment.percentage >= 5 && (
              <g
                className="transition-opacity duration-200 cursor-pointer"
                style={{
                  opacity: activeIndex !== null && activeIndex !== segment.index ? 0.4 : 1,
                }}
                onMouseEnter={() => setActiveIndex(segment.index)}
                onMouseLeave={() => setActiveIndex(null)}
                onClick={() => handleClick(segment.name)}
              >
                <text
                  x={segment.labelX}
                  y={segment.labelY}
                  textAnchor={segment.textAnchor as "start" | "middle" | "end"}
                  className="fill-muted-foreground text-[11px] font-medium"
                  dominantBaseline="middle"
                >
                  {segment.name}
                </text>
              </g>
            )}
          </g>
        ))}
        
        {/* Center text */}
        <text
          x={centerX}
          y={centerY - 10}
          textAnchor="middle"
          className="fill-foreground text-3xl font-semibold tabular-nums"
          dominantBaseline="middle"
        >
          {totalVisitors.toLocaleString()}
        </text>
        <text
          x={centerX}
          y={centerY + 18}
          textAnchor="middle"
          className="fill-muted-foreground text-xs font-medium"
          dominantBaseline="middle"
        >
          Unique Visitors
        </text>
        <text
          x={centerX}
          y={centerY + 34}
          textAnchor="middle"
          className="fill-muted-foreground/70 text-[10px]"
          dominantBaseline="middle"
        >
          {coverageLabel}
        </text>
      </svg>
      
      {/* Bottom legend for smaller segments or mobile */}
      <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-4 px-4">
        {chartData.map((item, index) => (
          <div 
            key={index} 
            className={`flex items-center gap-2 py-1.5 px-2 rounded-md cursor-pointer transition-all duration-150 ${
              activeIndex === index 
                ? 'bg-muted/60' 
                : 'hover:bg-muted/30'
            }`}
            onClick={() => handleClick(item.name)}
            onMouseEnter={() => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(null)}
          >
            <div 
              className="size-2.5 rounded-full shrink-0"
              style={{ backgroundColor: item.fill }}
            />
            <span className={`text-sm transition-colors duration-150 ${
              activeIndex === index ? 'text-foreground font-medium' : 'text-muted-foreground'
            }`}>
              {item.name}
            </span>
            <span className="font-semibold text-foreground tabular-nums text-sm">
              {item.percentage.toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}
